/**
 * Client-side telemetry — Zero query metrics, page loads, long tasks, errors.
 *
 * Creates real OTel spans via otel.ts, so everything flows through
 * a single pipeline (browser → /telemetry/traces → collector → Honeycomb)
 * and naturally participates in distributed traces.
 *
 * Gated on the API's CLIENT_TRACING_SECRET — if the secret is unset on the
 * server, the token endpoint returns 404 and nothing is collected.
 */

import type { Zero } from "@rocicorp/zero";
import { telemetryTokenPath } from "./apiPaths";

// ------------------------------------------------------------------
// State
// ------------------------------------------------------------------

export const sessionId = Math.random().toString(36).slice(2, 8);
let initialized = false;
let emitSpanFn:
  | ((
      name: string,
      attrs: Record<string, string | number>,
      durationMs?: number
    ) => void)
  | undefined;
let resetPageTraceFn:
  | ((attrs?: Record<string, string | number>) => void)
  | undefined;

// Page load tracking
let pageLoadStart = 0;
let pageLoadPath = "";
let activeQueryCount = 0;
let completedQueryCount = 0;
let pageLoadTotalBytes = 0;
let pageLoadSlowestQuery = "";
let pageLoadSlowestMs = 0;
let pageLoadCold = false;

// ------------------------------------------------------------------
// Span helpers
// ------------------------------------------------------------------

function emitSpan(
  name: string,
  attrs: Record<string, string | number>,
  durationMs?: number
) {
  emitSpanFn?.(
    name,
    {
      "session.id": sessionId,
      "page.path": location.pathname,
      ...attrs,
    },
    durationMs
  );
}

function errorAttributes(
  prefix: string,
  error: unknown
): Record<string, string> {
  if (error instanceof Error) {
    return {
      [`${prefix}.message`]: error.message.slice(0, 200),
      [`${prefix}.type`]: error.name,
    };
  }
  if (error == null || typeof error !== "object") {
    return { [`${prefix}.message`]: String(error).slice(0, 200) };
  }

  const attrs: Record<string, string> = {};
  if ("type" in error && typeof error.type === "string") {
    attrs[`${prefix}.type`] = error.type;
  }
  if ("kind" in error && typeof error.kind === "string") {
    attrs[`${prefix}.kind`] = error.kind;
  }
  if ("message" in error && typeof error.message === "string") {
    attrs[`${prefix}.message`] = error.message.slice(0, 200);
    return attrs;
  }

  attrs[`${prefix}.message`] = JSON.stringify(error).slice(0, 200);
  return attrs;
}

function errorAttributesWithLegacyMessage(
  prefix: string,
  legacyMessageKey: string,
  error: unknown
): Record<string, string> {
  const attrs = errorAttributes(prefix, error);
  return {
    ...attrs,
    [legacyMessageKey]: attrs[`${prefix}.message`] ?? "",
  };
}

// ------------------------------------------------------------------
// Public check
// ------------------------------------------------------------------

export function isTelemetryActive() {
  return initialized;
}

// ------------------------------------------------------------------
// Query tracking (called from useQuery wrapper)
// ------------------------------------------------------------------

export function trackQueryComplete(
  name: string,
  durationMs: number,
  rows: number,
  bytes: number,
  cached: boolean,
  firstDataMs: number | undefined
) {
  if (!initialized) return;
  emitSpan(
    "ui.query",
    {
      "zero.query.name": name,
      "zero.query.event": "complete",
      "zero.query.duration_ms": Math.round(durationMs),
      "zero.query.rows": rows,
      "zero.query.bytes": bytes,
      "zero.query.cached": cached ? 1 : 0,
      ...(firstDataMs !== undefined
        ? {
            "zero.query.first_data_ms": Math.round(firstDataMs),
            "zero.query.flush_gap_ms": Math.round(
              Math.max(0, durationMs - firstDataMs)
            ),
          }
        : {}),
    },
    durationMs
  );

  // Update page load tracking
  completedQueryCount++;
  pageLoadTotalBytes += bytes;
  if (!cached) pageLoadCold = true;
  if (durationMs > pageLoadSlowestMs) {
    pageLoadSlowestMs = durationMs;
    pageLoadSlowestQuery = name;
  }
  checkPageLoadComplete();
}

// ------------------------------------------------------------------
// Mutation tracking (called from ZeroSetup's mutate patch)
// ------------------------------------------------------------------

export function trackMutation(
  name: string,
  start: number,
  phase: "client" | "server",
  result: { type: string; error?: unknown }
) {
  if (!initialized) return;
  const durationMs = performance.now() - start;
  emitSpan(
    "ui.mutation",
    {
      "zero.mutation.name": name,
      "zero.mutation.phase": phase,
      "zero.mutation.result": result.type,
      "zero.mutation.duration_ms": Math.round(durationMs),
      ...(result.type === "error" && result.error
        ? errorAttributesWithLegacyMessage(
            "zero.mutation.error",
            "zero.mutation.error",
            result.error
          )
        : {}),
    },
    durationMs
  );
}

// ------------------------------------------------------------------
// One-off UI interaction events
// ------------------------------------------------------------------

export function trackUiEvent(
  name: string,
  attrs: Record<string, string | number> = {}
) {
  if (!initialized) return;
  emitSpan(name, attrs);
}

// Video tracking (called from video player elements)
// ------------------------------------------------------------------

export function trackVideoEvent(
  name: string,
  event: "play" | "pause" | "ended"
) {
  if (!initialized) return;
  emitSpan("ui.video", {
    "ui.video.name": name,
    "ui.video.event": event,
  });
}

// ------------------------------------------------------------------
// Connection state tracking
// ------------------------------------------------------------------

/** Subscribe to Zero connection state changes — disconnects, reconnects, errors. */
export function trackConnectionState(z: Zero) {
  let connectStart = 0;

  z.connection.state.subscribe((state) => {
    if (state.name === "connecting") {
      connectStart = performance.now();
      return;
    }

    if (state.name === "connected" && connectStart > 0) {
      const duration = performance.now() - connectStart;
      connectStart = 0;
      emitSpan(
        "ui.zero.connect",
        {
          "zero.connect.duration_ms": Math.round(duration),
        },
        duration
      );
      return;
    }

    if (
      state.name === "disconnected" ||
      state.name === "error" ||
      state.name === "needs-auth"
    ) {
      const reason = "reason" in state ? state.reason : undefined;
      emitSpan("ui.zero.connection", {
        "zero.connection.state": state.name,
        ...(reason
          ? errorAttributesWithLegacyMessage(
              "zero.connection.reason",
              "zero.connection.reason",
              reason
            )
          : {}),
      });
    }
  });
}

/** Called when a useQuery hook mounts (to track active query count per page). */
export function registerQuery() {
  activeQueryCount++;
}

/** Called when a useQuery hook unmounts. */
export function unregisterQuery() {
  activeQueryCount = Math.max(0, activeQueryCount - 1);
}

// ------------------------------------------------------------------
// Page load tracking
// ------------------------------------------------------------------

function resetPageLoad() {
  resetPageTraceFn?.({ "session.id": sessionId });
  pageLoadStart = performance.now();
  pageLoadPath = location.pathname;
  activeQueryCount = 0;
  completedQueryCount = 0;
  pageLoadTotalBytes = 0;
  pageLoadSlowestQuery = "";
  pageLoadSlowestMs = 0;
  pageLoadCold = false;
}

let pageLoadCheckScheduled = false;

function checkPageLoadComplete() {
  if (pageLoadCheckScheduled) return;
  pageLoadCheckScheduled = true;
  queueMicrotask(() => {
    pageLoadCheckScheduled = false;
    if (
      activeQueryCount > 0 &&
      completedQueryCount >= activeQueryCount &&
      pageLoadStart > 0
    ) {
      const duration = performance.now() - pageLoadStart;
      emitSpan(
        "ui.page-load",
        {
          "page.load.path": pageLoadPath,
          "page.load.duration_ms": Math.round(duration),
          "page.load.query_count": activeQueryCount,
          "page.load.total_bytes": pageLoadTotalBytes,
          "page.load.slowest_query": pageLoadSlowestQuery,
          "page.load.slowest_query_ms": Math.round(pageLoadSlowestMs),
          "page.load.cold": pageLoadCold ? 1 : 0,
        },
        duration
      );
      pageLoadStart = 0;
    }
  });
}

// ------------------------------------------------------------------
// Long task observer
// ------------------------------------------------------------------

function startLongTaskObserver() {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        emitSpan(
          "ui.longtask",
          { "ui.longtask.duration_ms": Math.round(entry.duration) },
          entry.duration
        );
      }
    });
    observer.observe({ type: "longtask", buffered: true });
  } catch {
    // longtask not supported in this browser
  }
}

function startPaintObservers() {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    const paintObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          emitSpan("ui.fcp", {
            "ui.fcp.time_ms": Math.round(entry.startTime),
          });
        }
      }
    });
    paintObserver.observe({ type: "paint", buffered: true });
  } catch {
    // paint timing not supported
  }
  // LCP fires multiple times as the largest candidate changes. Emit only
  // the final value on visibility change / page hide, per web vitals guidance.
  let lastLcp = 0;
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        lastLcp = entry.startTime;
      }
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
    const flushLcp = () => {
      if (lastLcp > 0) {
        emitSpan("ui.lcp", { "ui.lcp.time_ms": Math.round(lastLcp) });
        lastLcp = 0;
      }
    };
    addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushLcp();
    });
    addEventListener("pagehide", flushLcp);
  } catch {
    // LCP not supported
  }
}

// ------------------------------------------------------------------
// Error tracking
// ------------------------------------------------------------------

function startErrorTracking() {
  addEventListener("error", (event: ErrorEvent) => {
    emitSpan("ui.error", {
      ...errorAttributes("ui.error", event.error ?? event.message ?? "unknown"),
      "ui.error.source": "unhandled",
    });
  });

  addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    emitSpan("ui.error", {
      ...errorAttributes("ui.error", event.reason ?? "unknown"),
      "ui.error.source": "unhandled-rejection",
    });
  });
}

// ------------------------------------------------------------------
// Init
// ------------------------------------------------------------------

let initRequested = false;

export function initTracing(externalApiUrl: string, tenantId: string) {
  if (initRequested) return;
  initRequested = true;

  // Lazy imports to avoid pulling in environment.ts (which accesses document)
  // at module load time — that breaks Node-based test files that transitively
  // import this module via useQuery.
  Promise.all([import("../environment"), import("./otel")])
    .then(([{ config }, bt]) => {
      if (!config.tracingEnabled) return;
      return fetch(`${externalApiUrl}${telemetryTokenPath()}`, {
        credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { token: string; tracesEnabled?: boolean } | null) => {
          if (!data?.token || !data.tracesEnabled) return;

          bt.initBrowserTracing(data.token, externalApiUrl, tenantId);
          emitSpanFn = bt.emitSpan;
          resetPageTraceFn = bt.resetPageTrace;
          initialized = true;

          addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") bt.flushTraces();
          });

          addEventListener("popstate", () => resetPageLoad());
          resetPageLoad();

          startLongTaskObserver();
          startPaintObservers();
          startErrorTracking();
        });
    })
    .catch(() => {});
}
